
import { defineComponent, shallowRef } from "vue";
import { mapState } from 'pinia';
import moment from "moment";

import { useDashboardStore } from "~/store/dashboard.store";
import WidgetContentBase from '~/components/widgets/widget-content-base';
import { StationStats, StationStatsDistribution, StationStatsZoneDistribution } from "~/api/models/station-stats.model";
import { Chart, ChartData, TooltipItem } from "chart.js";
import { levelColor } from "~/variables";

const fake: StationStats = {
    period: {
        since: moment().subtract(1, 'w').unix(),
        before: moment().unix()
    },
    data: {
        'CO2': {
            min: 450,
            min_date: moment().subtract(10, 'h').unix(),
            max: 1200,
            max_date: moment().subtract(23, 'h').unix(),
            mean: 842,
            distribution: {
                1: {
                    "description": "< 600",
                    "time": 644410
                },
                2: {
                    "description": "< 1000",
                    "time": 147588
                },
                3: {
                    "description": "< 1500",
                    "time": 71972
                },
                4: {
                    "description": "< 2500",
                    "time": 0
                },
                5: {
                    "description": "> 2500",
                    "time": 0
                }
            }
        }
    },
}

export default defineComponent({

    extends: WidgetContentBase,

    data() {
        return {
            chart: null,
        };
    },

    mounted(): void {
        this.init();
    },

    computed: {
        ...mapState(useDashboardStore, ['currentStation', 'stats']),

        raw(): StationStats {
            return this.fake ? fake : this.stats;
        },

        distribution(): StationStatsZoneDistribution {
            if (!this.raw || !this.raw.data['CO2']) {
                return null;
            }
            return this.raw.data['CO2'].distribution;
        },

        datasets(): ChartData<'doughnut', number[], string> {
            const dist = this.distribution ? this.distribution : [];
            return {
                labels: Object.values(dist).map((ssd: StationStatsDistribution) => ssd.description),
                datasets: [{
                    label: this.$t('dashboard.distribution_widget.chart_title'),
                    data: Object.values(dist).map((ssd: StationStatsDistribution) => ssd.time),
                    backgroundColor: levelColor,
                }]
            };
        },

        total(): number {
            return this.distribution ? Object.values(this.distribution).reduce((acc: number, curr: StationStatsDistribution) => acc + curr.time, 0) : 0;
        },

        usage(): string {
            return this.$t('dashboard.distribution_widget.usage',{ usage: moment.duration(this.total, 'seconds').humanize()})
        }
    },

    methods: {
        init(): void {
            const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;
            if (!chartElement || this.chart) {
                return;
            }

            this.chart = shallowRef(new Chart(chartElement.getContext('2d'), {
                type: 'doughnut',
                data: this.datasets,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'right',
                        },
                        tooltip: {
                            callbacks: {

                                label: (context: TooltipItem<'doughnut'>) => {
                                    const seconds: number = context.parsed;
                                    const duration = moment.duration(seconds, 'seconds').humanize();
                                    return ` ${duration} (${context.label}ppm)`;
                                }
                            }
                        }
                    },
                }
            }));
        },

        updateChart() {
            try {

                if (!this.chart) {
                    this.init();
                    return;
                }

                this.chart.data = this.datasets;
                this.chart.update();
            } catch (e) {
                this.$notifyError(`An error occured while updating chart with options`, e);
            }
        }
    },

    watch: {

        async stats() {
            await this.updateChart();
        },
    }
})

